import { formatNumber, getMeasuringUnit } from './UnitConversions';

import { MeasuringSystem, ShotExtra } from '../SessionModel';

type ValidAverage = { isValid: boolean; value: number };

const averageValid = (shots: ShotExtra[], key: keyof ShotExtra): ValidAverage => {
  const activeShots = shots.filter((s: ShotExtra) => s.isActive);
  const validShots = activeShots.filter(s => {
    const v = s[key] as any;
    if (typeof v === 'object' && v !== null) {
      return v.valueString !== '---';
    }
    return v != null && !isNaN(v);
  });
  if (validShots.length === 0) return { isValid: false, value: 0 };
  const sum = validShots.reduce((acc, s) => {
    const v = s[key] as any;
    return acc + (typeof v === 'object' && v !== null ? v.value : v);
  }, 0);
  return { isValid: true, value: sum / validShots.length };
};

const formatOrDash = (avg: ValidAverage, fmt: (v: number) => string, isSquare?: boolean): string => {
  if (!avg.isValid) {
    return '---';
  }
  if (isSquare) {
    return fmt(avg.value).replace(/-/g, '');
  }
  return fmt(avg.value);
};

export const calculateAverage = (shots: ShotExtra[], measuringSystem: MeasuringSystem) => {
  const quality = averageValid(shots, 'shotQuality');
  const distanceToPin = averageValid(shots, 'distanceToPinConverted');
  const carryDistance = averageValid(shots, 'carryDistanceConverted');
  const spinRate = averageValid(shots, 'spinRateConverted');
  const ballSpeed = averageValid(shots, 'ballSpeedConverted');
  const clubSpeed = averageValid(shots, 'clubSpeedConverted');
  const smashFactor = averageValid(shots, 'smashFactorConverted');
  const apex = averageValid(shots, 'apexConverted');
  const launchAngle = averageValid(shots, 'launchAngleConverted');
  // KWSA-42: both are shown by the Simulator tab layout.
  const totalDistance = averageValid(shots, 'totalDistanceConverted');
  const descentAngle = averageValid(shots, 'descentAngleConverted');
  const clubPath = averageValid(shots, 'clubPathConverted');
  const faceAngle = averageValid(shots, 'faceAngleConverted');
  const attackAngle = averageValid(shots, 'attackAngleConverted');
  const faceToPath = averageValid(shots, 'faceToPathConverted');
  const spinAxis = averageValid(shots, 'spinAxisConverted');
  const horizontalLaunchAngle = averageValid(shots, 'horizontalLaunchAngleConverted');
  const sideAvg = averageValid(shots, 'sideConverted');
  const sideLabel = sideAvg.isValid ? getSide(sideAvg.value) : '';

  return {
    quality: {
      uom: '',
      value: formatOrDash(quality, v => v.toFixed()),
      label: 'QUALITY',
    },
    distanceToPin: {
      uom: getMeasuringUnit('distanceToPin', measuringSystem),
      value: formatOrDash(distanceToPin, v => v.toFixed()),
      label: 'PROXIMITY',
    },
    carryDistance: {
      uom: getMeasuringUnit('carryDistance', measuringSystem),
      value: formatOrDash(carryDistance, v => v.toFixed(1)),
      label: 'CARRY',
    },
    spinRate: {
      uom: getMeasuringUnit('spinRate', measuringSystem),
      value: formatOrDash(spinRate, v => formatNumber(v)),
      label: 'SPIN RATE',
    },
    ballSpeed: {
      uom: getMeasuringUnit('ballSpeed', measuringSystem),
      value: formatOrDash(ballSpeed, v => v.toFixed(1)),
      label: 'BALL SPEED',
    },
    clubSpeed: {
      uom: getMeasuringUnit('clubSpeed', measuringSystem),
      value: formatOrDash(clubSpeed, v => v.toFixed(1)),
      label: 'CLUB SPEED',
    },
    smashFactor: {
      uom: '',
      value: formatOrDash(smashFactor, v => v.toFixed(2)),
      label: 'SMASH FACTOR',
    },
    apex: {
      uom: getMeasuringUnit('apex', measuringSystem),
      value: formatOrDash(apex, v => v.toFixed(1)),
      label: 'APEX',
    },
    launchAngle: {
      uom: '',
      value: formatOrDash(launchAngle, v => `${v.toFixed(1)}°`),
      label: 'LAUNCH ANGLE',
    },
    totalDistance: {
      uom: getMeasuringUnit('totalDistance', measuringSystem),
      value: formatOrDash(totalDistance, v => v.toFixed(1)),
      label: 'TOTAL',
    },
    descentAngle: {
      uom: '',
      value: formatOrDash(descentAngle, v => `${v.toFixed(1)}°`),
      label: 'DESCENT ANGLE',
    },

    clubPath: {
      uom: clubPath.isValid ? getSideFull(clubPath.value, 1) : '',
      value: formatOrDash(clubPath, v => `${v.toFixed(1)}°`, getSideFull(clubPath.value, 1) === 'SQUARE'),
      label: 'CLUB PATH',
    },
    faceAngle: {
      uom: faceAngle.isValid ? getSideFull(faceAngle.value, 1) : '',
      value: formatOrDash(faceAngle, v => `${v.toFixed(1)}°`, getSideFull(faceAngle.value, 1) === 'SQUARE'),
      label: 'FACE ANGLE',
    },
    attackAngle: {
      uom: attackAngle.isValid ? getUpDownFull(attackAngle.value, 1) : '',
      value: formatOrDash(attackAngle, v => `${v.toFixed(1)}°`, getUpDownFull(attackAngle.value, 1) === 'SQUARE'),
      label: 'ATTACK ANGLE',
    },
    faceToPath: {
      uom: faceToPath.isValid ? getSideFull(faceToPath.value, 1) : '',
      value: formatOrDash(faceToPath, v => `${v.toFixed(1)}°`, getSideFull(faceToPath.value, 1) === 'SQUARE'),
      label: 'FACE TO PATH',
    },

    spinAxis: {
      uom: spinAxis.isValid ? getSideFull(spinAxis.value, 1) : '',
      value: formatOrDash(spinAxis, v => `${Math.abs(v).toFixed(1)}°`, getSideFull(spinAxis.value, 1) === 'SQUARE'),
      label: 'SPIN AXIS',
    },
    horizontalLaunchAngle: {
      uom: horizontalLaunchAngle.isValid ? getSideFull(horizontalLaunchAngle.value, 1) : '',
      value: formatOrDash(
        horizontalLaunchAngle,
        v => `${Math.abs(v).toFixed(1)}°`,
        getSideFull(horizontalLaunchAngle.value, 1) === 'SQUARE',
      ),
      label: 'HORIZ. ANGLE',
    },
    side: {
      uom: getMeasuringUnit('side', measuringSystem),
      // KWSA-38: one decimal place, matching the per-shot Side Carry values.
      value: sideAvg.isValid ? `${Math.abs(sideAvg.value).toFixed(1)} ${sideLabel}` : '---',
      label: 'SIDE CARRY',
    },
  };
};

export const average = (shots: ShotExtra[], key: keyof ShotExtra, isCheckActive = true): number => {
  let activeShots = shots;
  if (isCheckActive) {
    activeShots = shots.filter((shot: ShotExtra) => shot.isActive);
  }
  let sum: number = activeShots.reduce((accSum: number, shot: ShotExtra) => {
    const shotValue = shot[key] as any;
    if (typeof shotValue === 'object' && shotValue !== null) {
      return (accSum += (shot[key] as any).value);
    }
    return (accSum += shot[key] as any);
  }, 0);
  return sum / activeShots.length;
};

const getSide = (value: number) => {
  let side = '';
  if (value > 0) side = 'R';
  if (value < 0) side = 'L';
  return side;
};

const getSideFull = (value: number, fixed: number) => {
  const rounded = parseFloat(value.toFixed(fixed));
  let side = 'SQUARE';
  if (rounded > 0) side = 'RIGHT';
  if (rounded < 0) side = 'LEFT';
  return side;
};

const getUpDownFull = (value: number, fixed: number) => {
  const rounded = parseFloat(value.toFixed(fixed));
  let side = 'SQUARE';
  if (rounded > 0) side = 'UP';
  if (rounded < 0) side = 'DOWN';
  return side;
};
